import { useEffect, useState } from 'react';
import { Cloud, CloudDrizzle, CloudFog, CloudLightning, CloudRain, CloudSun, CloudSunRain, Sun } from 'lucide-react';
import { useLang } from '../i18n';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ICONS = {
  clear: Sun,
  partly: CloudSun,
  overcast: Cloud,
  fog: CloudFog,
  drizzle: CloudDrizzle,
  rain: CloudRain,
  showers: CloudSunRain,
  thunder: CloudLightning,
};

// Island/destination names used by the site → configured weather location keys.
export const WEATHER_KEY_OF_ISLAND = {
  'São Tomé': 'sao-tome',
  'Príncipe': 'principe',
  'Santiago': 'praia',
  'São Vicente': 'mindelo',
  'Sal': 'sal',
  'Boa Vista': 'boa-vista',
};

export const WEATHER_KEY_OF_DESTINATION = {
  'São Tomé': 'sao-tome',
  'Príncipe': 'principe',
  'Cabo Verde': 'praia',
  'São Tomé + Príncipe': 'sao-tome',
  'São Tomé e Príncipe': 'sao-tome',
};

/**
 * Compact, informational destination weather card (Open-Meteo via backend proxy).
 * - Informational only: never feeds prices, availability or quotations.
 * - On any failure (API down, timeout, malformed data) renders nothing — the page
 *   must never break because of weather.
 */
export const DestinationWeather = ({ locationKey, date = '', testid = 'destination-weather', className = '' }) => {
  const { t } = useLang();
  const [state, setState] = useState({ loading: true, data: null });

  useEffect(() => {
    if (!locationKey) return undefined;
    let cancelled = false;
    setState({ loading: true, data: null });
    const qs = date ? `?date=${encodeURIComponent(date)}` : '';
    fetch(`${API}/weather/${locationKey}${qs}`)
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`weather ${r.status}`))))
      .then((data) => {
        if (cancelled) return;
        if (typeof data?.temperature_c !== 'number') throw new Error('malformed weather payload');
        setState({ loading: false, data });
      })
      .catch(() => { if (!cancelled) setState({ loading: false, data: null }); });
    return () => { cancelled = true; };
  }, [locationKey, date]);

  if (!locationKey) return null;

  if (state.loading) {
    return (
      <div data-testid={`${testid}-loading`} className={`inline-flex items-center gap-3 rounded-2xl border border-eco-ink/10 bg-white/70 px-4 py-3 ${className}`}>
        <span className="h-7 w-7 animate-pulse rounded-full bg-eco-ink/10" />
        <span className="space-y-1.5">
          <span className="block h-4 w-16 animate-pulse rounded bg-eco-ink/10" />
          <span className="block h-3 w-28 animate-pulse rounded bg-eco-ink/5" />
        </span>
      </div>
    );
  }

  const data = state.data;
  if (!data) return null; // silent fallback — weather is optional information

  const Icon = ICONS[data.condition] || Cloud;
  const isForecast = data.type === 'forecast' && !data.forecast_out_of_range;
  const secondary = isForecast
    ? [
        data.temperature_min_c != null ? `${Math.round(data.temperature_min_c)}° / ${Math.round(data.temperature_c)}°` : null,
        data.precipitation_probability_pct != null ? `${t('wx.precip')} ${data.precipitation_probability_pct}%` : null,
      ].filter(Boolean).join(' · ')
    : [
        data.feels_like_c != null ? t('wx.feels', { v: Math.round(data.feels_like_c) }) : null,
        data.humidity_pct != null ? `${t('wx.humidity')} ${data.humidity_pct}%` : null,
        data.wind_kmh != null ? `${t('wx.wind')} ${Math.round(data.wind_kmh)} km/h` : null,
      ].filter(Boolean).join(' · ');

  return (
    <div data-testid={testid} role="status" aria-live="polite" className={`inline-flex max-w-full flex-col gap-1 ${className}`}>
      <div className="inline-flex items-center gap-3 rounded-2xl border border-eco-ink/10 bg-white/80 px-4 py-3 shadow-sm backdrop-blur">
        <Icon size={26} className="shrink-0 text-eco-gold" aria-hidden="true" />
        <div className="min-w-0">
          <p className="text-lg font-bold leading-none text-eco-ink" data-testid={`${testid}-temp`}>{Math.round(data.temperature_c)}°C</p>
          <p className="mt-1 truncate text-xs font-medium text-eco-ink/60" data-testid={`${testid}-summary`}>
            {data.name} · {t(`wx.${data.condition}`)}
          </p>
          {secondary && <p className="mt-0.5 truncate text-[10px] text-eco-ink/45" data-testid={`${testid}-secondary`}>{secondary}</p>}
        </div>
      </div>
      <p className="pl-1 text-[10px] font-medium uppercase tracking-[0.12em] text-eco-ink/40" data-testid={`${testid}-caption`}>
        {isForecast ? t('wx.forecastFor', { date: data.date }) : t('wx.current')}
      </p>
    </div>
  );
};
